import { jsx as _jsx } from "react/jsx-runtime";
import { getSpinnerClasses } from '#pplx/frontend/aether/components/Spinner/classes.js';
import { useMeasureableHandle } from '#pplx/frontend/aether/internal/useMeasureableHandle.js';
import { useReducedMotion } from '#pplx/frontend/common/hooks/useReducedMotion.js';
import React, { useEffect, useState } from 'react';
// --- Braille variant internals ---
const BRAILLE_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];
const BRAILLE_INTERVAL_MS = 80;
// Explicit class map required for Tailwind's content scanner to detect these classes.
const BRAILLE_COLOR_CLASSES = {
    default: 'text-default',
    quiet: 'text-secondary',
    quieter: 'text-tertiary',
    super: 'text-super-primary',
};
function BrailleSpinner({ ref, size, color, }) {
    const reducedMotion = useReducedMotion();
    const [frame, setFrame] = useState(0);
    useEffect(() => {
        if (reducedMotion) {
            setFrame(0);
            return;
        }
        const id = setInterval(() => setFrame((prev) => (prev + 1) % BRAILLE_FRAMES.length), BRAILLE_INTERVAL_MS);
        return () => clearInterval(id);
    }, [reducedMotion]);
    // Always render the first frame when reduced motion is preferred.
    // This guards against a race condition where Playwright's reducedMotion
    // emulation hasn't been applied yet when the component first mounts,
    // causing the setInterval to briefly advance the frame before the
    // useEffect can reset it.
    const displayFrame = reducedMotion ? 0 : frame;
    return (_jsx("span", { ref: ref, className: `inline-flex items-center justify-center leading-none shrink-0 ${BRAILLE_COLOR_CLASSES[color]}`, style: { width: size, height: size, fontSize: size }, children: BRAILLE_FRAMES[displayFrame] }));
}
// --- Main component ---
/**
 * Animated loading spinner using CSS conic-gradient and rotate animation. Supports multiple sizes
 * and color variants.
 *
 * @category components
 * @keywords spinner, components, animated, loading, using, conic-gradient, rotate, animation,
 *   supports, multiple, sizes, color, default, quiet, quieter, super, variants, size, variant,
 *   progress, button, banner
 * @whenToUse Use Spinner to indicate an indeterminate loading state where the duration or progress
 *   is unknown, such as fetching data, waiting for a server response, or processing a request. The
 *   variant prop controls the visual style: 'circular' (default) renders a spinning gradient ring
 *   suitable for standard UI, while 'braille' renders an animated sequence of Unicode braille
 *   characters ideal for terminal-style or developer-facing interfaces. For operations with known
 *   progress, use Progress instead.
 * @guidelineDo
 *   - Use the variant prop to choose the visual style — 'circular' (default) for standard graphical
 *     loading indicators and 'braille' for text-based or terminal-style interfaces
 *   - Use the size prop to match the spinner scale to its surrounding context — 16px (default) for
 *     inline loading, larger values for page-level or section-level loading
 *   - Use the color prop to match the spinner to its visual context — 'default' for standard
 *     content areas, 'quiet' or 'quieter' for subtle backgrounds, 'super' for primary/brand-colored
 *     loading
 *   - Add an aria-label or visually hidden text alongside the spinner to communicate the loading
 *     state to screen readers
 *   - Use the braille variant in developer tools, CLI-style UIs, or terminal emulators where a
 *     text-based spinner fits the aesthetic
 * @guidelineDont
 *   - Don't use Spinner for determinate loading where progress is known — use Progress instead for
 *     file uploads or multi-step operations
 *   - Don't render spinners without conveying loading context to screen readers — pair with
 *     aria-busy on the loading region or a visually hidden label
 *   - Don't use very large spinner sizes (> 48px) — for full-page loading states, center a
 *     standard-sized spinner with supporting text
 *   - Don't use the braille variant in standard UI contexts where users expect graphical elements —
 *     it renders animated Unicode braille characters (⠋⠙⠹⠸⠼⠴⠦⠧⠇⠏) that may look out of place in
 *     polished interfaces
 *   - Don't mix variants within the same loading context — use one variant consistently for visual
 *     coherence
 * @accessibility
 *   - Spinner is a visual-only indicator — it does not include ARIA roles by default, so the parent
 *     container should set aria-busy='true' and optionally role='status' to communicate the loading
 *     state to screen readers
 *   - The circular variant uses a conic-gradient CSS animation with a radial mask, which is purely
 *     decorative and does not affect the accessibility tree
 *   - The braille variant renders animated Unicode braille characters (⠋⠙⠹⠸⠼⠴⠦⠧⠇⠏) which screen
 *     readers may attempt to announce — use aria-hidden='true' on the spinner element if a separate
 *     loading announcement is provided
 *   - The braille variant uses explicit color class mappings (text-default, text-secondary,
 *     text-tertiary, text-super-primary) for Tailwind's content scanner, ensuring correct styling in both
 *     light and dark modes
 *   - The spin animation (circular variant) and interval-based frame cycling (braille variant)
 *     should both respect the user's prefers-reduced-motion preference when the host application
 *     applies the appropriate media query styles
 * @relatedComponents progress, button, banner
 */
export function Spinner({ ref, size = 16, color = 'default', variant = 'circular', }) {
    const targetRef = useMeasureableHandle(ref);
    if (variant === 'braille') {
        return (_jsx(BrailleSpinner, { ref: targetRef, size: size, color: color }));
    }
    return (_jsx("div", { ref: targetRef, className: getSpinnerClasses({ color }), style: {
            width: size,
            backgroundImage: 'conic-gradient(from 270deg, transparent 0%, currentColor 99%)',
            mask: `radial-gradient(transparent 0%, transparent ${size / 2 - 2}px, black ${size / 2 - 2}px, black) center center`,
        } }));
}
//# sourceMappingURL=index.js.map